import { useMemo, useState, type ReactNode } from "react";
import { generators, type Generator, type RequestSpec } from "@rest/codegen";
import CodeViewer, { type SyntaxLang } from "./CodeViewer";

// Map each generator's plain-text `lang` slug to the CodeViewer's
// SyntaxLang. Falls back to "plain" when we don't have a highlighter.
function viewerLang(g: { lang: string }): SyntaxLang {
  switch (g.lang) {
    case "shell":
    case "bash":       return "shell";
    case "javascript": return "javascript";
    case "typescript": return "typescript";
    case "python":     return "python";
    case "go":         return "go";
    case "ruby":       return "ruby";
    case "php":        return "php";
    case "java":       return "java";
    case "csharp":     return "csharp";
    case "rust":       return "rust";
    case "swift":      return "swift";
    case "powershell": return "powershell";
    default:           return "plain";
  }
}

interface Props {
  spec: RequestSpec;
  initialId?: string;
  onClose: () => void;
}

const STORAGE_KEY = "rest.codegenChoice";

// Per-request "show me this in <language>" picker. Side panel of languages
// on the left, generated code + copy button on the right.
export default function CodeExport({ spec, initialId, onClose }: Props) {
  const stored = (() => { try { return localStorage.getItem(STORAGE_KEY); } catch { return null; } })();
  const [id, setId] = useState<string>(initialId ?? stored ?? "javascript-fetch");
  const [copied, setCopied] = useState(false);

  const active = useMemo(() => generators.find((g) => g.id === id) ?? generators[0], [id]);
  const code = useMemo(() => active.generate(spec), [active, spec]);

  // Group the sidebar so curl gets its own header above the language
  // list. Without this, cURL is just the top row of a long list and
  // people miss it.
  const shellGens = useMemo(() => generators.filter((g) => g.lang === "shell" || g.lang === "powershell"), []);
  const langGens = useMemo(() => generators.filter((g) => g.lang !== "shell" && g.lang !== "powershell"), []);

  const choose = (newId: string) => {
    setId(newId);
    try { localStorage.setItem(STORAGE_KEY, newId); } catch { /* ignore */ }
  };

  return (
    <div className="fixed inset-0 z-[120] grid place-items-center p-4" style={{ background: "var(--backdrop)" }} onClick={onClose}>
      <div className="card w-full max-w-4xl shadow-[var(--shadow-strong)] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} style={{ maxHeight: "85vh" }}>
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 className="text-lg font-semibold">Copy as code</h2>
          <button className="btn" onClick={onClose}>Close</button>
        </div>

        <div className="flex flex-1 min-h-0">
          <aside className="w-56 shrink-0 border-r border-border bg-surface-2 overflow-y-auto scroll-pane">
            <div className="p-1 text-sm space-y-2">
              <SidebarSection label="Shell">
                {shellGens.map((g) => (
                  <SidebarItem key={g.id} g={g} active={g.id === id} onPick={() => choose(g.id)} />
                ))}
              </SidebarSection>
              <SidebarSection label="Languages">
                {langGens.map((g) => (
                  <SidebarItem key={g.id} g={g} active={g.id === id} onPick={() => choose(g.id)} />
                ))}
              </SidebarSection>
            </div>
          </aside>

          <div className="flex-1 min-w-0 flex flex-col">
            {active.hint && (
              <div className="px-4 py-2 border-b border-border bg-surface-2 text-xs muted-strong">
                <span className="muted">Setup:</span> <code className="mono">{active.hint}</code>
              </div>
            )}
            <div className="flex-1 min-h-0 min-w-0 p-3">
              <CodeViewer value={code} lang={viewerLang(active)} maxHeight="100%" />
            </div>
            <div className="border-t border-border px-3 py-2 flex items-center gap-3">
              {copied && <span className="chip bg-good/15 text-good">Copied!</span>}
              <span className="ml-auto" />
              <button
                type="button"
                className="btn-primary"
                onClick={async () => {
                  const text = active.copyGenerate ? active.copyGenerate(spec) : code;
                  try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }
                  catch { /* ignore */ }
                }}
              >Copy to clipboard</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SidebarSection({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="px-2 pt-1 pb-0.5 text-[10px] uppercase tracking-wider muted-strong font-semibold">{label}</div>
      <ul>{children}</ul>
    </div>
  );
}

function SidebarItem({ g, active, onPick }: { g: Generator; active: boolean; onPick: () => void }) {
  return (
    <li>
      <button
        type="button"
        onClick={onPick}
        className={
          "w-full text-left px-3 py-2 rounded-md " +
          (active ? "bg-accent text-[var(--ink-on-accent)]" : "text-text-dim hover:bg-surface-3 hover:text-text")
        }
      >{g.label}</button>
    </li>
  );
}
